import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the Prompt 101 Thailand Jobs Employers/Jobs API.
// Every query param is optional - visibility scoping (Agent/PLC/SuperAgent/
// SuperAdmin) always happens on the backend from the caller's own session,
// never from a value this file sends. This file never calls a matching/
// assignment/tracking API (none exist), never calls the Invite Card API
// (out of scope for this side of the platform), and never accepts or sends
// a worker id anywhere - Thailand Jobs Employers/Jobs only ever creates or
// edits employer/job demand records, and Mark Ready For Matching
// only ever flips a job's own demandStatus.

const EMPLOYERS_PATH = "/backoffice/thailand-jobs/employers";
const JOBS_PATH = "/backoffice/thailand-jobs/jobs";

// Exactly the param names the P101 contract whitelists - anything else
// passed to listEmployers is silently dropped rather than forwarded, so a
// typo'd key (or a future filter added to the UI without updating this
// whitelist) can never accidentally reach the backend as an unfiltered
// pass-through.
const EMPLOYER_LIST_PARAM_KEYS = ["page", "pageSize", "search", "employerStatus", "agentId", "superAgentOrganizationId", "plcOrganizationId"];

// Same whitelist convention, for GET /thailand-jobs/jobs (Prompt 103B) -
// exactly the param names BackofficeThailandJobsController.ListJobs
// declares, in the same order as the controller signature.
const JOB_LIST_PARAM_KEYS = [
  "page",
  "pageSize",
  "search",
  "jobStatus",
  "demandStatus",
  "employerId",
  "agentId",
  "superAgentOrganizationId",
  "plcOrganizationId",
  "skillCode",
];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`), restricted to `paramKeys`. */
function buildQuery(paramKeys, params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of paramKeys) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function listEmployers(params = {}) {
  return apiClient.get(`${EMPLOYERS_PATH}${buildQuery(EMPLOYER_LIST_PARAM_KEYS, params)}`);
}

// Detail fetch requires a real, non-empty employerId - this file never
// issues a request for a missing/blank id (the caller only calls this once
// a row/card is actually clicked).
export function getEmployer(employerId) {
  if (!employerId) {
    throw new Error("getEmployer requires an employerId");
  }
  return apiClient.get(`${EMPLOYERS_PATH}/${encodeURIComponent(employerId)}`);
}

// request is the exact CreateEmployerRequest shape (agentId, legalName,
// displayName, taxId, businessType, primaryContactName, primaryPhoneNumber,
// lineContact, addressText) - never a worker id field, never an invite
// card field.
export function createEmployer(request) {
  return apiClient.post(EMPLOYERS_PATH, request);
}

// request is the exact UpdateEmployerRequest shape (PATCH semantics: every
// field optional, null/omitted keeps the existing stored value) - never a
// worker id field, never an agentId (ownership is not transferable through
// this endpoint), never an invite card field.
export function updateEmployer(employerId, request) {
  if (!employerId) {
    throw new Error("updateEmployer requires an employerId");
  }
  return apiClient.patch(`${EMPLOYERS_PATH}/${encodeURIComponent(employerId)}`, request);
}

/**
 * P160: the closed vocabularies the detailed employer form fills its dropdowns
 * from. Fetched rather than hardcoded here on purpose - the API validates
 * against the same constants, and a frontend copy would drift into offering
 * values the server then rejects. Read-only reference data: no employer id,
 * no worker id, no filter of any kind.
 */
export function getEmployerOptions() {
  return apiClient.get(`${EMPLOYERS_PATH}/options`);
}

// request is the exact CreateJobRequest shape - agentId/employerId
// identify the job-demand record's ownership, never a worker id
// (there is no such field on this request at all, and this file never adds
// one).
export function createJob(request) {
  return apiClient.post(JOBS_PATH, request);
}

// --- Jobs (Prompt 103B) ---

export function listJobs(params = {}) {
  return apiClient.get(`${JOBS_PATH}${buildQuery(JOB_LIST_PARAM_KEYS, params)}`);
}

// Detail fetch requires a real, non-empty jobId - this file never issues a
// request for a missing/blank id (the caller only calls this once a
// row/card is actually clicked).
export function getJob(jobId) {
  if (!jobId) {
    throw new Error("getJob requires a jobId");
  }
  return apiClient.get(`${JOBS_PATH}/${encodeURIComponent(jobId)}`);
}

// request is the exact UpdateJobRequest shape (PATCH semantics: every field
// optional, null/omitted keeps the existing value) - never a worker id
// field (there is no such field on this request at all), never changes
// jobStatus/demandStatus as a side effect of an otherwise-unrelated field
// edit (the caller only ever sends the demand fields the Edit Job form
// actually collects).
export function updateJob(jobId, request) {
  if (!jobId) {
    throw new Error("updateJob requires a jobId");
  }
  return apiClient.patch(`${JOBS_PATH}/${encodeURIComponent(jobId)}`, request);
}

// The one status-changing write this file exposes for Jobs. Requires a
// real, non-empty jobId for the same reason as getJob above. Sends no
// request body: the real endpoint (POST .../jobs/{jobId}/ready-for-matching)
// takes no payload, only the id in the path (mirrors
// ../readyForMatch/readyForMatchApi.js's markReadyForMatch). This call only
// ever flips the job's own demandStatus to ReadyForMatching (and jobStatus
// from Draft/Paused to Open if needed) - it never creates a
// dbo.WmxWorkerAssignments row and never starts tracking/device binding,
// regardless of what the backend response looks like.
export function markJobReadyForMatching(jobId) {
  if (!jobId) {
    throw new Error("markJobReadyForMatching requires a jobId");
  }
  return apiClient.post(`${JOBS_PATH}/${encodeURIComponent(jobId)}/ready-for-matching`);
}

// ============================================================
// P161: employer media - logo, cover image, attached documents
// ============================================================

/**
 * The two images an employer record carries. Mirrors EmployerImageKinds in the
 * backend; each maps to its own route, so a typo here cannot silently write the
 * wrong slot.
 */
export const EMPLOYER_IMAGE_KIND_LOGO = "logo";
export const EMPLOYER_IMAGE_KIND_COVER = "cover-image";

/**
 * Attaches the company logo or the cover/background image.
 *
 * Sent as multipart FormData under the field name the route binds ("image") -
 * passed to apiClient.postForm untouched so the browser sets the multipart
 * boundary itself. Never base64 in a JSON body: the endpoint takes a real file,
 * and hand-encoding one would inflate it by a third and bypass the server's own
 * magic-number check.
 */
export function uploadEmployerImage(employerId, imageKind, file) {
  if (!employerId) {
    throw new Error("uploadEmployerImage requires an employerId");
  }
  if (imageKind !== EMPLOYER_IMAGE_KIND_LOGO && imageKind !== EMPLOYER_IMAGE_KIND_COVER) {
    throw new Error(`uploadEmployerImage got an unknown imageKind: ${imageKind}`);
  }
  if (!file) {
    throw new Error("uploadEmployerImage requires a file");
  }
  const formData = new FormData();
  formData.append("image", file);
  return apiClient.postForm(`${EMPLOYERS_PATH}/${encodeURIComponent(employerId)}/${imageKind}`, formData);
}

/** Attaches one document plus the operator's own free-text label for it. */
export function uploadEmployerDocument(employerId, file, documentType) {
  if (!employerId) {
    throw new Error("uploadEmployerDocument requires an employerId");
  }
  if (!file) {
    throw new Error("uploadEmployerDocument requires a file");
  }
  const formData = new FormData();
  formData.append("document", file);
  if (documentType) {
    formData.append("documentType", documentType);
  }
  return apiClient.postForm(`${EMPLOYERS_PATH}/${encodeURIComponent(employerId)}/documents`, formData);
}

/** Soft-deletes one attached document. Never touches the logo or the cover image. */
export function deleteEmployerDocument(employerId, documentId) {
  if (!employerId || !documentId) {
    throw new Error("deleteEmployerDocument requires an employerId and a documentId");
  }
  return apiClient.delete(`${EMPLOYERS_PATH}/${encodeURIComponent(employerId)}/documents/${encodeURIComponent(documentId)}`);
}

/**
 * An employer image's real bytes, as a blob.
 *
 * Read through apiClient.getBlob rather than by pointing an <img src> straight
 * at the URL: the endpoint requires the session bearer token, which a plain
 * <img> request would never carry, so the image would silently 401. The caller
 * turns the blob into an object URL and is responsible for revoking it.
 */
export function getEmployerImageBlob(employerId, imageKind) {
  if (!employerId) {
    throw new Error("getEmployerImageBlob requires an employerId");
  }
  return apiClient.getBlob(`${EMPLOYERS_PATH}/${encodeURIComponent(employerId)}/${imageKind}/file`);
}

/** One attached document's real bytes, for download/preview. */
export function getEmployerDocumentBlob(employerId, documentId) {
  if (!employerId || !documentId) {
    throw new Error("getEmployerDocumentBlob requires an employerId and a documentId");
  }
  return apiClient.getBlob(`${EMPLOYERS_PATH}/${encodeURIComponent(employerId)}/documents/${encodeURIComponent(documentId)}/file`);
}

